import React, { useContext } from 'react';
import { Chip, InputAdornment } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { toLocalDate } from 'src/util/partialDate';
import { LocalizationProvider } from '@mui/x-date-pickers';
// date-fns@2.x is pinned in this project; use the V2 adapter path (the
// default AdapterDateFns in @mui/x-date-pickers v8 expects date-fns v3+).
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { validateDate } from '@mui/x-date-pickers/validation';
import { ThemeContext } from 'src/store/ContextStore';
import { ModalDateFieldContext } from './ModalDateFieldContext';

const defaultInvalidDateMessage = 'Invalid date.';

// Nothing below 1600 is a plausible clinical date, and a floor is what lets a
// half-typed year be recognised as transient rather than stored.
const DEFAULT_MIN_DATE = new Date(1600, 0, 1);

// Fallbacks for a value held back from the form. A caller that has a better
// sentence for its own field passes `minDateMessage` / `maxDateMessage`.
const HELD_BACK_MESSAGES = {
  invalidDate: defaultInvalidDateMessage,
  minDate: 'Date is earlier than allowed.',
  maxDate: 'Date is later than allowed.',
  disableFuture: 'Date cannot be in the future.',
  disablePast: 'Date cannot be in the past.',
  shouldDisableDate: 'That date cannot be selected.',
  shouldDisableMonth: 'That month cannot be selected.',
  shouldDisableYear: 'That year cannot be selected.'
};

// Compare pickers' values by CONTENT, never by object identity: `toLocalDate`
// builds a new Date on every call and several callers (PartialDateInput) build
// theirs inline on every render, so identity says "changed" when nothing did.
const contentKey = (date) => {
  if (!(date instanceof Date)) return date ?? null;
  const time = date.getTime();
  return Number.isNaN(time) ? 'invalid' : time;
};

export default function DateInput(props) {
  const {
    value,
    onChange,
    disableFuture,
    fullWidth,
    disabled,
    minDateMessage,
    maxDateMessage,
    minDate,
    allowDateSelection = true,
    error,
    helperText,
    slotProps = {},
    showTodayAction
  } = props;
  const effectiveFullWidth = fullWidth ?? true;

  const { theme } = useContext(ThemeContext);
  let format = props.format ?? theme.dateFormat;

  // Translate the stored moment-style tokens to date-fns ones for the picker.
  // The dot format used to map to SLASHES, so an admin who chose DD.MM.YYYY got
  // dd/MM/yyyy everywhere — the separator was silently discarded. Display
  // surfaces avoid this table entirely by formatting with moment, which takes
  // the stored tokens verbatim (see src/hooks/useDateFormatter.js).
  if (format == 'MM/DD/YYYY') format = 'MM/dd/yyyy';
  if (format == 'DD.MM.YYYY') format = 'dd.MM.yyyy';
  if (format == 'DD/MM/YYYY') format = 'dd/MM/yyyy';

  // These were computed and then never passed to the DatePicker, so
  // `allowDateSelection` was inert everywhere it was used and a group that had
  // configured year-only capture still got a full day-and-month calendar
  // (TGCLOUD-1632).
  //
  // The view is named 'day' in @mui/x-date-pickers v9 — 'date' was the v5
  // spelling and is rejected by the propType. ['year','day'] is also v9's own
  // default (DatePicker/shared.js defaultViews), so the allowDateSelection
  // branch below is a no-op for every consumer that does not opt out.
  //
  // openTo must be passed too: it defaults to 'day', and when 'day' is absent
  // from `views` MUI console.warns and falls back to views[0].
  const views = allowDateSelection ? ['year', 'day'] : ['year'];
  const openTo = allowDateSelection ? 'day' : 'year';

  // ---------------------------------------------------------------------------
  // Semi-controlled value (TGCLOUD-1718)
  //
  // `draft` is what the picker is given, and it echoes back EVERY value MUI
  // publishes; only values that are safe to keep are passed on to the form.
  //
  // Both halves are load-bearing, because MUI's field does not hold its own
  // copy of a date it has published — it relies on the controlled value coming
  // back down (@mui/x-date-pickers 9.10.0, internals/hooks/useField/useFieldState.js):
  //
  //   :267-279  a valid date typed into an empty field is published, and a 0ms
  //             timer is armed that CLEARS every section unless the `value`
  //             prop changed in the meantime;
  //   :286-289  an invalid one (31/06, 29/02 in a non-leap year) is published
  //             without touching the sections at all — the typed digit is
  //             applied only by the patch path at :320-324, which again needs
  //             the `value` prop to change;
  //   :320      that prop is compared with `!==`, i.e. by object identity, and
  //             the sections are rebuilt from whatever it holds.
  //
  // Hence the three rules this component now keeps:
  //
  //   1. Never swallow a publish. TGCLOUD-1650 dropped the transient years
  //      below the floor (12/05/0001 on the way to 12/05/1980) to stop them
  //      reaching the form, which left the prop null and let the 0ms timer wipe
  //      the day and month the user had already typed — the reported defect.
  //   2. Never pass a value MUI cannot vouch for to the form. That is what let
  //      an invalid combo typed over a saved date blank it (formatDate of an
  //      Invalid Date is ''), and what let `disableFuture` dates be stored.
  //   3. Never hand the picker a new object for an unchanged date, or any
  //      re-render of the parent — the questionnaire autosaves 800ms after a
  //      keystroke — rebuilds the sections and eats a part-typed date.
  // ---------------------------------------------------------------------------
  const parentDate = toLocalDate(value);
  const [draft, setDraft] = React.useState(parentDate);
  const [heldBackMessage, setHeldBackMessage] = React.useState('');
  const heldBackErrorRef = React.useRef(null);

  // Adopt the incoming value whenever the FORM's value changes — first load, a
  // reset, an autosave echo — but not when it merely re-renders, and not when
  // it is echoing back the value this component just published.
  //
  // The previous-value marker is state and not a ref on purpose. React may
  // discard a render (a sibling suspending, StrictMode), and a ref written
  // during render survives that while the setState beside it is rolled back —
  // which would leave the marker saying "already adopted" for a value that
  // never was, and the field showing the old date for good.
  const [lastParentKey, setLastParentKey] = React.useState(
    contentKey(parentDate)
  );
  const parentKey = contentKey(parentDate);
  if (lastParentKey !== parentKey) {
    setLastParentKey(parentKey);
    if (contentKey(draft) !== parentKey) {
      setDraft(parentDate);
      heldBackErrorRef.current = null;
      setHeldBackMessage('');
    }
  }

  const messageForHeldBack = (validationError) => {
    if (!validationError) return '';
    if (validationError === 'minDate' && minDateMessage) return minDateMessage;
    if (
      (validationError === 'maxDate' || validationError === 'disableFuture') &&
      maxDateMessage
    )
      return maxDateMessage;
    return HELD_BACK_MESSAGES[validationError] ?? defaultInvalidDateMessage;
  };

  const onDateChange = (date, context) => {
    const validationError = context?.validationError ?? null;

    // Echo unconditionally — see rules 1 and 2 above.
    setDraft(date);
    heldBackErrorRef.current = validationError;
    if (validationError) return;

    setHeldBackMessage('');
    onChange(date);
  };

  // Leaving the field is where a held-back value is resolved: the message
  // appears, and the field snaps back to what the form actually holds.
  //
  // Both halves matter. Feedback waits until blur because almost every date
  // typed passes through a complete but out-of-range state (12/05/0001 on the
  // way to 1980), so reporting live would flash an error on nearly every
  // entry. And snapping back is what keeps the field honest: a rejected value
  // left on screen reads as saved, and a consumer that gates its save on form
  // state — FamilyDetailsForm's validDates, PersonMenu's — would then save the
  // old date with the new one still showing. Reverting makes the rejection
  // visible instead. PartialDateInput's year box already resolves an
  // out-of-range year the same way.
  const onFieldBlur = (event) => {
    // focusout bubbles as the caret moves between the day, month and year
    // sections; only a move out of the field itself is leaving the field.
    if (event.currentTarget?.contains?.(event.relatedTarget)) return;
    const heldBack = heldBackErrorRef.current;
    setHeldBackMessage(messageForHeldBack(heldBack));
    if (heldBack) {
      heldBackErrorRef.current = null;
      setDraft(parentDate);
    }
  };

  const callerTextFieldProps = slotProps.textField ?? {};
  const inModal = useContext(ModalDateFieldContext);
  const effectiveMinDate = toLocalDate(minDate) || DEFAULT_MIN_DATE;
  const todayActionEnabled =
    (showTodayAction ?? inModal) &&
    allowDateSelection &&
    !props.readOnly &&
    !disabled;
  const adapterRef = React.useRef(null);
  const getAdapter = () => {
    if (!adapterRef.current) adapterRef.current = new AdapterDateFns();
    return adapterRef.current;
  };

  const onTodayClick = () => {
    const adapter = getAdapter();
    const today = adapter.startOfDay(adapter.date());

    const validationError = validateDate({
      adapter,
      value: today,
      timezone: 'default',
      props: { minDate: effectiveMinDate, disableFuture }
    });

    if (validationError) {
      heldBackErrorRef.current = null;
      setHeldBackMessage(messageForHeldBack(validationError));
      setDraft(parentDate);
      return;
    }

    heldBackErrorRef.current = null;
    setHeldBackMessage('');
    setDraft(today);
    onChange(today);
  };

  const todayActionRef = React.useRef(null);
  todayActionRef.current = onTodayClick;

  const todaySlots = React.useMemo(
    () => ({
      inputAdornment: function InputAdornmentWithToday({
        children,
        ...adornmentProps
      }) {
        return (
          <InputAdornment {...adornmentProps}>
            {adornmentProps.position === 'end' && (
              <Chip
                component="button"
                type="button"
                size="small"
                label="TODAY"
                title="Set to today"
                clickable
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  todayActionRef.current();
                }}
                sx={(muiTheme) => ({
                  mr: 0.5,
                  height: 22,
                  px: 0,
                  py: 0,
                  border: 0,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  lineHeight: 1,
                  color: 'primary.main',
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                  '&:hover, &:focus-visible': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.2)
                  },
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0,
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 1,
                    overflow: 'visible',
                    marginRight: '-0.06em'
                  }
                })}
              />
            )}
            {children}
          </InputAdornment>
        );
      }
    }),
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        readOnly={props.readOnly}
        disabled={disabled}
        // The draft, not `value`: a date the form has not accepted still has to
        // be reflected here or MUI clears the sections under the user.
        value={draft}
        onChange={onDateChange}
        disableFuture={disableFuture}
        format={format}
        views={views}
        openTo={openTo}
        // toLocalDate, not new Date(): a bare 'YYYY-MM-DD' parses as UTC
        // midnight, which renders the previous day at negative UTC offsets.
        minDate={effectiveMinDate}
        slots={todayActionEnabled ? todaySlots : undefined}
        slotProps={{
          textField: {
            error: !!error || !!heldBackMessage,
            helperText: heldBackMessage || (error ? helperText : ''),
            fullWidth: effectiveFullWidth,
            ...callerTextFieldProps,
            onBlur: (event) => {
              callerTextFieldProps.onBlur?.(event);
              onFieldBlur(event);
            }
          }
        }}
      />
    </LocalizationProvider>
  );
}
